import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, Star, MapPin, Calendar, Users, ChevronRight, Music, Disc, UserPlus, UserCheck, Video, Share2, Flag, Radio } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import { ArtistSupportButton } from '../components/producer/SupportButton';
import HybridSupportModal from '../components/HybridSupportModal';
import { API_BASE as API } from '../lib/apiBase';


function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

function getBookingLine(artist, lang) {
  const bp = artist?.booking_profile;
  if (bp) {
    if (bp.pricing_mode === 'inquire') {
      return lang === 'es'
        ? (bp.inquire_cta_es || 'Consultar disponibilidad y presupuesto de contratación')
        : (bp.inquire_cta_en || 'Inquire about availability and booking budget');
    }
    if (bp.list_price_cents != null) {
      const e = (bp.list_price_cents / 100).toFixed(0);
      if (bp.pricing_mode === 'from') {
        return lang === 'es' ? `Desde ${e} € / show` : `From ${e} € / show`;
      }
      return `${e} € / show`;
    }
  }
  const p = artist?.price_per_show ?? 0;
  return lang === 'es' ? `Disponibilidad desde: ${p}€ / show` : `Availability from: ${p}€ / show`;
}
function getArtistLocationLabel(artist) {
  const bp = artist?.booking_profile;
  if (bp?.location?.city || bp?.location?.country) {
    return [bp.location?.city, bp.location?.country].filter(Boolean).join(', ');
  }
  return artist?.city || 'España';
}

export default function ArtistPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { playSong } = usePlayer();
  const { token } = useAuth();
  const [artist, setArtist] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [localFollowers, setLocalFollowers] = useState(0);
  const [showSupport, setShowSupport] = useState(false);
  const [supportAmt, setSupportAmt] = useState('');
  const [supportMsg, setSupportMsg] = useState('');
  const [supportStatus, setSupportStatus] = useState('');
  const [recentSupports, setRecentSupports] = useState([]);
  const [showHybridSupport, setShowHybridSupport] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/artists/${id}`).then(r => r.json()),
      fetch(`${API}/api/recommendations/similar/${id}`).then(r => r.json()),
    ]).then(([a, s]) => {
      setArtist(a);
      setLocalFollowers(a.followers || 0);
      setSimilar(s.similar || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!token || !id) return;
    fetch(`${API}/api/users/favorites`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const favIds = (data.favorites || []).map(a => a.id);
        setIsFav(favIds.includes(id));
      }).catch(() => {});
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setIsFollowing((data.following || []).includes(id));
      }).catch(() => {});
  }, [token, id]);

  const toggleFav = async () => {
    if (!token) return;
    const res = await fetch(`${API}/api/users/favorites/${id}`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setIsFav(data.action === 'added');
  };

  const toggleFollow = async () => {
    if (!token) return;
    const res = await fetch(`${API}/api/artists/${id}/follow`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (data.action === 'followed') {
      setIsFollowing(true);
      setLocalFollowers(f => f + 1);
    } else {
      setIsFollowing(false);
      setLocalFollowers(f => Math.max(0, f - 1));
    }
  };

  useEffect(() => {
    if (id) fetch(`${API}/api/apoyos/artist/${id}`).then(r => r.json()).then(d => setRecentSupports(d.apoyos || [])).catch(() => {});
  }, [id]);

  const sendSupport = async (amt) => {
    const val = parseFloat(amt || supportAmt);
    if (!val || !token) return;
    setSupportStatus('sending');
    try {
      const res = await fetch(`${API}/api/apoyos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ artist_id: id, amount_fiat: val, currency: 'EUR', message: supportMsg })
      });
      const data = await res.json();
      if (res.ok) {
        setSupportStatus('ok');
        setSupportAmt(''); setSupportMsg('');
        setTimeout(() => { setSupportStatus(''); setShowSupport(false); }, 2000);
        fetch(`${API}/api/apoyos/artist/${id}`).then(r => r.json()).then(d => setRecentSupports(d.apoyos || [])).catch(() => {});
      } else {
        setSupportStatus(data.detail || 'Error');
      }
    } catch (e) { setSupportStatus('Error'); }
  };

  const startArtistRadio = async () => {
    try {
      const res = await fetch(`${API}/api/discover/radio?artist_id=${id}&limit=20`, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      const data = await res.json();
      const radioSongs = data.songs || [];
      if (radioSongs.length > 0) playSong(radioSongs[0], radioSongs, 0);
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen pt-14 flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!artist) return (
    <div className="min-h-screen pt-14 flex items-center justify-center text-pulse-muted">
      Artist not found
    </div>
  );

  const bio = lang === 'en' ? (artist.bio_en || artist.bio_es) : artist.bio_es;
  const songs = artist.songs || [];
  const events = artist.upcoming_events || [];
  const topSong = songs[0];

  return (
    <div data-testid="artist-page" className="min-h-screen pb-32">
      {/* Cover */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img src={artist.cover} alt={artist.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pulse-bg/40 to-pulse-bg" />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 80%, rgba(0,212,255,0.06) 0%, transparent 50%)'
        }} />
      </div>

      <div className="px-4 md:px-12 -mt-24 relative z-10">
        {/* Profile info */}
        <div className="flex items-end gap-6">
          <img
            src={artist.photo}
            alt={artist.name}
            className="w-28 h-28 md:w-36 md:h-36 rounded-2xl object-cover border-4 border-pulse-bg shadow-xl"
          />
          <div className="pb-2">
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight">{artist.name}</h1>
            {artist.tagline && <p className="mt-2 text-sm md:text-base text-white/80 max-w-2xl">{artist.tagline}</p>}
            <div className="flex items-center gap-4 mt-2 text-sm text-pulse-muted">
              <span className="flex items-center gap-1 text-amber-400">&#9835; {formatNum(artist.corcheas_received || 0)} corcheas</span>
              <span className="flex items-center gap-1"><Play size={14} /> {formatNum(artist.plays)} {t('home.plays')}</span>
              <span className="flex items-center gap-1"><Users size={14} /> {formatNum(localFollowers)} {t('artist.followers')}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span data-testid="artist-booking-availability" className="text-sm rounded-full border border-pulse-border bg-pulse-surface px-4 py-2 text-pulse-muted">
                <span className="text-white font-semibold">{getBookingLine(artist, lang)}</span>
              </span>
              {artist.available && (
                <span className="text-xs bg-green-500/15 border border-green-500/20 text-green-300 px-3 py-2 rounded-full">{lang === 'es' ? 'Disponible para propuesta' : 'Open for proposals'}</span>
              )}
              <span className="text-xs bg-pulse-surface border border-pulse-border text-pulse-muted px-3 py-2 rounded-full inline-flex items-center gap-1">
                <MapPin size={12} /> {getArtistLocationLabel(artist)}
              </span>
            </div>

            <p className="max-w-2xl text-sm text-pulse-muted leading-relaxed">
              {lang === 'es'
                ? 'Escucha primero, guarda si conecta contigo y, si tiene sentido, solicita propuesta con una capa operativa protegida.'
                : 'Listen first, save if it resonates, and only if it makes sense request a protected proposal.'}
            </p>

            <div className="flex flex-wrap gap-2">
              {topSong && (
                <button
                  data-testid="artist-play-top-track"
                  onClick={() => playSong(topSong, songs, 0)}
                  className="bg-pulse-primary text-black font-bold px-6 py-3 rounded-full hover:bg-pulse-primary-hover transition-colors duration-200 inline-flex items-center gap-2"
                >
                  <Play size={16} /> {lang === 'es' ? 'Escuchar ahora' : 'Listen now'}
                </button>
              )}
              <button
                data-testid="artist-radio-button"
                onClick={startArtistRadio}
                className="border border-pulse-border text-white px-6 py-3 rounded-full hover:border-pulse-primary hover:text-pulse-primary transition-colors duration-200 inline-flex items-center gap-2"
              >
                <Radio size={16} /> {lang === 'es' ? 'Radio del artista' : 'Artist radio'}
              </button>
              <Link
                to={`/booking/${artist.id}`}
                data-testid="hire-btn"
                className="border border-yellow-500/30 text-yellow-300 px-6 py-3 rounded-full hover:bg-yellow-500/10 transition-colors duration-200 inline-flex items-center gap-2"
              >
                {lang === 'es' ? 'Solicitar propuesta' : 'Request proposal'}
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {token && (
              <>
                <button
                  data-testid="fav-btn"
                  onClick={toggleFav}
                  className={`p-2.5 rounded-xl border transition-all duration-200 ${isFav ? 'bg-amber-500/20 border-amber-500/30 text-amber-400' : 'bg-pulse-surface border-pulse-border text-pulse-muted hover:border-amber-500/30'}`}
                >
                  <Star size={18} fill={isFav ? 'currentColor' : 'none'} />
                </button>
                <button
                  data-testid="follow-btn"
                  onClick={toggleFollow}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 ${isFollowing ? 'bg-pulse-primary/20 border-pulse-primary/30 text-pulse-primary' : 'bg-pulse-surface border-pulse-border text-pulse-muted hover:border-pulse-primary/30'}`}
                >
                  {isFollowing ? <UserCheck size={16} /> : <UserPlus size={16} />}
                  {isFollowing ? (lang === 'es' ? 'Soy fan' : 'Fan') : (lang === 'es' ? 'Hacerme fan' : 'Become a fan')}
                </button>
                <ArtistSupportButton
                  artistId={artist.id}
                  initialCount={artist.corcheas_received || 0}
                  artistName={artist.name}
                />
              </>
            )}
            <button onClick={() => {
              const url = window.location.href;
              const text = `${artist.name} en PulseMusic`;
              if (navigator.share) { navigator.share({ title: text, url }).catch(() => {}); }
              else { navigator.clipboard.writeText(`${text} ${url}`); alert(lang === 'es' ? 'Link copiado!' : 'Link copied!'); }
            }} data-testid="share-artist-btn" className="p-3 border border-pulse-border rounded-xl text-pulse-muted hover:text-pulse-primary hover:border-cyan-500/30 transition-colors" title={lang === 'es' ? 'Compartir' : 'Share'}>
              <Share2 size={16} />
            </button>
            {token && (
              <button onClick={async () => {
                const reason = window.prompt(lang === 'es' ? 'Motivo del reporte (copyright, contenido inapropiado, spam, otro):' : 'Report reason:');
                if (!reason) return;
                await fetch(`${API}/api/report`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content_type: 'artist', content_id: artist.id, reason, description: reason }) });
                alert(lang === 'es' ? 'Reporte enviado. Gracias.' : 'Report sent. Thank you.');
              }} data-testid="report-artist-btn" className="p-3 border border-pulse-border rounded-xl text-pulse-muted hover:text-red-400 hover:border-red-500/30 transition-colors" title={lang === 'es' ? 'Reportar' : 'Report'}>
                <Flag size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Catálogo' : 'Catalog'}</p>
            <p className="text-sm text-white">{songs.length} {lang === 'es' ? 'canciones listas para escuchar' : 'tracks ready to play'}</p>
          </div>
          <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Escena' : 'Scene'}</p>
            <p className="text-sm text-white">{events.length > 0 ? `${events.length} ${lang === 'es' ? 'eventos próximos' : 'upcoming events'}` : (lang === 'es' ? 'Sin eventos publicados todavía' : 'No published events yet')}</p>
          </div>
          <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Propuesta' : 'Proposal'}</p>
            <p className="text-sm text-white">{lang === 'es' ? 'Con pago protegido y seguimiento operativo' : 'Protected payment and operations follow-up'}</p>
          </div>
        </div>

        {(artist.event_types?.length > 0 || artist.instagram || artist.tiktok || artist.website || artist.youtube) && (
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Ideal para' : 'Best for'}</p>
              <div className="flex flex-wrap gap-2">
                {(artist.event_types || []).map((item) => <span key={item} className="rounded-full border border-pulse-border px-3 py-1 text-xs bg-pulse-elevated">{item}</span>)}
              </div>
            </div>
            <div className="rounded-2xl border border-pulse-border bg-pulse-surface p-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Presencia digital' : 'Digital presence'}</p>
              <div className="flex flex-wrap gap-3 text-sm text-pulse-muted">
                {artist.instagram && <a href={artist.instagram} target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">Instagram</a>}
                {artist.tiktok && <a href={artist.tiktok} target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">TikTok</a>}
                {artist.youtube && <a href={artist.youtube} target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">YouTube</a>}
                {artist.website && <a href={artist.website} target="_blank" rel="noreferrer" className="hover:text-white transition-colors duration-200">Web</a>}
              </div>
            </div>
          </div>
        )}

        {/* Bio */}
        <p className="text-pulse-muted text-sm leading-relaxed mt-6 max-w-2xl">{bio}</p>

        {/* Genres */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {artist.genre?.map(g => (
            <span key={g} className="text-xs px-3 py-1 bg-pulse-surface border border-pulse-border rounded-full text-pulse-muted">{g}</span>
          ))}
          <span className="text-xs px-3 py-1 bg-pulse-surface border border-pulse-border rounded-full text-pulse-muted flex items-center gap-1">
            <MapPin size={10} /> {artist.city}
          </span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 mt-4">
          {[1,2,3,4,5].map(i => (
            <Star key={i} size={16} className={i <= Math.round(artist.rating) ? 'text-yellow-400' : 'text-pulse-border'} fill={i <= Math.round(artist.rating) ? 'currentColor' : 'none'} />
          ))}
          <span className="text-sm text-pulse-muted ml-2">{artist.rating?.toFixed(1)}</span>
        </div>

        {/* Support */}
        {token && (
          <div className="mt-6 bg-pulse-surface border border-pulse-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-sm flex items-center gap-2"><span className="text-amber-400">&#9835;</span> {lang === 'es' ? 'Apoyar a este artista' : 'Support this artist'}</h3>
              <button onClick={() => setShowHybridSupport(true)} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-xs px-4 py-2 rounded-xl font-semibold transition" data-testid="open-hybrid-support">{lang === 'es' ? 'Apoyar' : 'Support'}</button>
            </div>
            <p className="text-xs text-pulse-muted mb-3">{lang === 'es' ? 'Puedes apoyar con corcheas o EUR directo. El artista recibe el 85%.' : 'Support with corcheas or EUR. Artist receives 85%.'}</p>
            {recentSupports.length > 0 && (
              <div className="pt-3 border-t border-pulse-border">
                <p className="text-[10px] text-pulse-muted mb-1">{lang === 'es' ? 'Apoyos recientes' : 'Recent support'}</p>
                {recentSupports.slice(0, 3).map((d, i) => (
                  <p key={i} className="text-xs text-pulse-muted"><span className="text-amber-400">{d.from_user_name}</span> - {d.amount_corcheas || d.amount || 0} corcheas {d.message && <span className="italic">"{d.message}"</span>}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {showHybridSupport && artist && (
          <HybridSupportModal
            artist={artist}
            onClose={() => setShowHybridSupport(false)}
            onSuccess={() => {
              fetch(`${API}/api/apoyos/artist/${id}`).then(r => r.json()).then(d => setRecentSupports(d.apoyos || [])).catch(() => {});
            }}
          />
        )}

        {/* Videos */}
        {artist.videos && artist.videos.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
              <Video size={18} className="text-pulse-primary" /> Videoclips
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {artist.videos.map((v, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-pulse-border bg-black">
                  <video src={v.url} controls className="w-full aspect-video" poster={v.thumbnail} />
                  {v.title && <p className="p-2 text-sm">{v.title}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming Events */}
        {events.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
              <Calendar size={18} className="text-pulse-primary" /> {t('artist.upcoming')}
            </h2>
            <div className="space-y-2">
              {events.map(ev => {
                const evTitle = lang === 'en' ? (ev.title_en || ev.title_es) : ev.title_es;
                return (
                  <Link
                    key={ev.id}
                    to="/events"
                    className="flex items-center gap-4 p-3 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-all duration-200"
                  >
                    <div className="w-12 h-12 bg-pulse-elevated rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar size={18} className="text-pulse-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{evTitle}</p>
                      <p className="text-xs text-pulse-muted">{ev.location?.venue} - {new Date(ev.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' })}</p>
                    </div>
                    <ChevronRight size={16} className="text-pulse-muted" />
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Songs */}
        <section className="mt-10">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
            <Music size={18} className="text-pulse-primary" /> {t('artist.songs')}
          </h2>
          <div className="space-y-2">
            {songs.map((song, idx) => {
              const songTitle = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
              return (
                <button
                  key={song.id}
                  data-testid={`play-song-${song.id}`}
                  onClick={() => playSong(song, songs, idx)}
                  className="w-full flex items-center gap-4 p-3 bg-pulse-surface border border-transparent hover:border-cyan-500/30 rounded-xl transition-all duration-200 text-left group"
                >
                  <span className="text-pulse-muted text-sm w-6 text-right">{idx + 1}</span>
                  <img src={song.cover} alt={songTitle} className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{songTitle}</p>
                    <p className="text-xs text-pulse-muted">{Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-pulse-muted">
                    <span>{formatNum(song.plays)}</span>
                    <span className="text-amber-400">&#9835;</span>
                    <span className="text-amber-400">{formatNum(song.corcheas_received || 0)}</span>
                  </div>
                  <Play size={16} className="text-pulse-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </button>
              );
            })}
          </div>
        </section>

        {/* Similar Artists */}
        {similar.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
              <Disc size={18} className="text-pulse-primary" /> {t('artist.similar')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {similar.map(s => (
                <Link
                  key={s.id}
                  to={`/artist/${s.id}`}
                  className="bg-pulse-surface border border-pulse-border rounded-xl overflow-hidden hover:border-cyan-500/30 transition-all duration-200 group"
                >
                  <div className="aspect-square overflow-hidden">
                    <img src={s.photo} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <p className="font-heading font-semibold text-sm truncate">{s.name}</p>
                    <p className="text-xs text-pulse-muted">{s.genre?.slice(0, 2).join(', ')}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
